/**
 * forecast.js — Cash flow projection algorithm
 *
 * Projects checking balance day-by-day for N days into the future.
 * Plots payday income bumps and bill payment drops to answer
 * "will we make it?" at a glance.
 *
 * Uses the same payday anchor logic as dates.js (biweekly +14 day steps).
 */

/**
 * Get all paydays that fall within a date range [start, end] inclusive.
 * Walks the biweekly anchor forward/backward to find paydays in window.
 *
 * @param {string} anchor - ISO date string of a known payday (e.g., "2026-02-25")
 * @param {Date} startDate - Start of range (inclusive)
 * @param {Date} endDate - End of range (inclusive)
 * @returns {Date[]} Array of payday dates within the range
 */
export function getPaydaysInRange(anchor, startDate, endDate) {
  const anchorDate = new Date(anchor + 'T00:00:00');
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

  // Walk anchor backward until before start
  let payday = new Date(anchorDate);
  while (payday > start) {
    payday.setDate(payday.getDate() - 14);
  }

  // Now walk forward, collecting paydays in [start, end]
  const paydays = [];
  while (payday <= end) {
    if (payday >= start) {
      paydays.push(new Date(payday));
    }
    payday.setDate(payday.getDate() + 14);
  }

  return paydays;
}

/**
 * Get the last day of a given month.
 * @param {number} year
 * @param {number} month - 0-indexed (0=Jan, 11=Dec)
 * @returns {number} Last day of the month (28-31)
 */
function lastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Generate a day-by-day cash flow forecast.
 *
 * @param {Object} params
 * @param {number} params.checkingBalance - Current checking account balance
 * @param {Array} params.bills - Active bills with { id, name, amount, due_day, current_cycle_paid, frequency }
 * @param {string} params.paydayAnchor - ISO date string of a known payday
 * @param {number} params.conservativeIncome - Expected net income per paycheck
 * @param {Date} [params.today] - Reference date (defaults to now, useful for testing)
 * @param {number} [params.days=30] - Number of days to forecast
 * @returns {Array<{ date: Date, balance: number, events: Array<{ type: 'bill'|'payday', name: string, amount: number }> }>}
 */
export function generateForecast({ checkingBalance, bills, paydayAnchor, conservativeIncome, today = new Date(), days = 30 }) {
  const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + days);

  // Pre-compute paydays in the forecast window
  const paydays = getPaydaysInRange(paydayAnchor, startDate, endDate);
  const paydaySet = new Set(paydays.map(d => d.getTime()));

  // Filter to monthly active bills only
  const activeBills = (bills || []).filter(b => b.active !== false && b.due_day);

  // Track which bill+month combos have been processed to prevent double-counting
  const processedBills = new Set();

  let balance = checkingBalance || 0;
  const forecast = [];

  for (let i = 0; i <= days; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const events = [];
    const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const dayOfMonth = d.getDate();
    const lastDay = lastDayOfMonth(d.getFullYear(), d.getMonth());

    // Check for payday
    if (paydaySet.has(d.getTime())) {
      balance += conservativeIncome;
      events.push({ type: 'payday', name: 'Payday', amount: conservativeIncome });
    }

    // Check for bills due today
    for (const bill of activeBills) {
      // Clamp due_day to last day of month (e.g., due_day 31 in Feb → 28)
      const effectiveDueDay = Math.min(bill.due_day, lastDay);

      if (effectiveDueDay !== dayOfMonth) continue;

      const billMonthKey = `${bill.id}-${monthKey}`;
      if (processedBills.has(billMonthKey)) continue;
      processedBills.add(billMonthKey);

      // For current month (i === 0 day or same month as today), skip already-paid bills
      const isCurrentMonth = d.getMonth() === startDate.getMonth() && d.getFullYear() === startDate.getFullYear();
      if (isCurrentMonth && bill.current_cycle_paid) continue;

      balance -= bill.amount;
      events.push({ type: 'bill', name: bill.name, amount: bill.amount });
    }

    forecast.push({ date: new Date(d), balance, events });
  }

  return forecast;
}
